import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MODELS } from '../RbacConfig';
import { HbrTab, HbrTabGroup } from '@harbor/elements-react-standalone';
import AdminTab from './AdminTab';
import TileBuilder from '../TileBuilder/TileBuilder';
import DatabaseViewer from '../DatabaseViewer';
import TaskManager from '../TaskManager/TaskManager';
import AIAdmin from '../AI/AIAdmin';
import { ADMIN_URL_TOOLBOX_CONFIG } from './admin-values';
import NavHistoryTracker from '../../../navigation/NavHistoryTracker';
import { useAdminPermissions } from '../Hooks/useAdminPermissions';

const toolboxConfigModels = {
  customTiles: MODELS.customTiles,
  calendarEvents: MODELS.calendarEvents,
  banners: MODELS.banners,
  'db-viewer': {
    key: 'db-viewer',
    label: 'DB Viewer',
    pageView: 'page.dbViewer.view',
  },
  queue: {
    key: 'queue',
    label: 'Queue & Tasks',
    pageView: 'page.queueTasksAdmin.view',
  },
  ai: {
    key: 'ai',
    label: 'AI',
    pageView: 'page.aiAdmin.view',
  },
};

const AdminToolboxConfig = () => {
  const { section, tab1, tab2, tab3, tab4 } = useParams();
  const navigate = useNavigate();
  const { isVisibleTab } = useAdminPermissions();

  const modelKeys = useMemo(
    () => Object.keys(toolboxConfigModels).filter((key) => isVisibleTab(toolboxConfigModels[key])),
    [isVisibleTab]
  );

  const validTab = useMemo(
    () => (section && modelKeys.includes(section) ? section : modelKeys[0]),
    [section, modelKeys]
  );

  const [currentTabKey, setCurrentTabKey] = useState<string | undefined>(validTab);

  useEffect(() => {
    setCurrentTabKey(validTab);
  }, [validTab]);

  const handleTabClick = (key: string) => {
    setCurrentTabKey(key);
    navigate(`${ADMIN_URL_TOOLBOX_CONFIG}/${key}`);
  };

  if (!modelKeys.length) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          minWidth: 0,
        }}
      >
        <h1 className="mt-0 pb-6 border-b border-gray-15 text-gray-60 font-semibold leading-5">
          Toolbox Configuration
        </h1>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <NavHistoryTracker title={tab4 ?? tab3 ?? tab2 ?? tab1 ?? currentTabKey} />

      <h1 className="mt-0 pb-6 border-b border-gray-15 text-gray-60 font-semibold leading-5">
        Toolbox Configuration
      </h1>

      <HbrTabGroup
        primary
        className="my-4"
        style={{
          display: 'block',
          flexShrink: 0,
        }}
      >
        {modelKeys.map((key) => (
          <HbrTab
            slot="nav"
            panel={key}
            key={key}
            onClick={() => handleTabClick(key)}
            active={key === currentTabKey}
          >
            {toolboxConfigModels[key].label}
          </HbrTab>
        ))}
      </HbrTabGroup>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {currentTabKey === 'customTiles' && (
          <AdminTab
            model={toolboxConfigModels.customTiles}
            DialogComponent={TileBuilder}
          />
        )}

        {currentTabKey === 'calendarEvents' && (
          <AdminTab model={toolboxConfigModels.calendarEvents} />
        )}

        {currentTabKey === 'banners' && (
          <AdminTab model={toolboxConfigModels.banners} />
        )}

        {currentTabKey === 'db-viewer' && (
          <div
            style={{
              width: '100%',
              height: 'calc(100vh - 260px)',
              minHeight: 650,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <DatabaseViewer isVisible />
          </div>
        )}

        {currentTabKey === 'queue' && <TaskManager />}

        {currentTabKey === 'ai' && <AIAdmin />}
      </div>
    </div>
  );
};

export default AdminToolboxConfig;